import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Digit-group color palette (matches W57)
const dgColors = [
  'bg-slate-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-orange-500', 'bg-rose-500', 'bg-purple-500', 'bg-indigo-500',
];

/**
 * W57 Calendar Date Final Verdict Card — extracted from ConsolidatedVerdict.js.
 * Self-contained: fetches /api/analysis/date-digit-analysis on mount.
 */
export default function W57VerdictCard() {
  const [w57, setW57] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/analysis/date-digit-analysis`, { withCredentials: true })
      .then(r => setW57(r.data))
      .catch(() => {});
  }, []);

  if (!w57) return null;
  const proj = (w57.dg_projection || []).slice(0, 10);
  const top3Digits = proj.slice(0, 3);
  const recs = w57.top_recommended_numbers || [];
  if (recs.length === 0) return null;

  // Reappearance boost (same logic as W57 Block 9)
  const freqMap = {};
  const pool = w57?.number_overlap?.target_pool_detail || [];
  for (const p of pool) freqMap[p.number] = p.freq;

  const enriched = recs.map(n => {
    const rfreq = freqMap[n.number] || 0;
    let boost = 0;
    if (rfreq >= 3) boost = 0.6;
    else if (rfreq === 2) boost = 0.3;
    else if (rfreq === 1) boost = 0.1;
    return { ...n, reappearance_freq: rfreq, final_score: (n.strength || 0) + boost };
  });
  enriched.sort((a, b) => b.final_score - a.final_score);
  const finalSeven = enriched.slice(0, 7);

  const tier = (i) => {
    if (i < 3) return { ring: 'ring-4 ring-amber-300', bg: 'bg-gradient-to-br from-amber-500 to-orange-600', label: 'ELITE' };
    if (i < 5) return { ring: 'ring-2 ring-emerald-300', bg: 'bg-gradient-to-br from-emerald-500 to-teal-600', label: 'STRONG' };
    return { ring: 'ring-2 ring-blue-300', bg: 'bg-gradient-to-br from-blue-500 to-indigo-600', label: 'SUPPORT' };
  };

  return (
    <section className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border-4 border-amber-400 rounded-xl overflow-hidden shadow-2xl" data-testid="w57-verdict-card">
      <header className="px-5 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-block px-2.5 py-1 bg-slate-900 text-amber-300 rounded text-[11px] font-black tracking-wider">W17 · FINAL</span>
          <h2 className="text-base font-black text-white tracking-tight">CALENDAR DATE FINAL VERDICT — {w57.month_name} {w57.day}</h2>
          <span className="ml-auto text-[10px] text-white/90 font-mono">Next draw: {w57.next_draw_date}</span>
        </div>
        <p className="text-[10px] text-white/90 mt-1">
          Top-3 projected digit groups (with hit %) + Top 7 numbers (with pool-match %) — composite of digit projection × pool weight × reappearance frequency.
        </p>
      </header>

      {/* Projected Digits — full top 10 with % */}
      <div className="px-5 py-3 bg-slate-800 border-b border-slate-700">
        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-2">★ Projected Digits (chance %) — top-3 = digit verdict</div>
        <div className="flex items-center gap-2 flex-wrap">
          {proj.map((p, i) => {
            const isTop3 = i < 3;
            return (
              <div key={p.digit} className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${isTop3 ? 'bg-emerald-900/40 border-emerald-400' : 'bg-slate-700 border-slate-600 opacity-60'}`} data-testid={`w57-proj-digit-${p.digit}`}>
                <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-black text-xs text-white ${dgColors[p.digit]} ${isTop3 ? 'ring-2 ring-amber-300' : ''}`}>{p.digit}</span>
                <span className={`text-xs font-mono font-bold ${isTop3 ? 'text-emerald-200' : 'text-slate-400'}`}>{p.chance_pct}%</span>
                {isTop3 && <span className="text-[9px] font-black text-amber-300 ml-0.5">#{i + 1}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Final 7 picks with % */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">★ Top 7 Numbers (pool-match %)</div>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-emerald-300 font-mono">
              avg pool-match {Math.round(finalSeven.reduce((s, n) => s + (n.pool_match_rate_pct || 0), 0) / Math.max(finalSeven.length, 1))}%
            </span>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {finalSeven.map((n, i) => {
            const t = tier(i);
            const driverDigit = top3Digits.find(d => d.digit === n.digit_group);
            return (
              <div key={n.number} className="text-center" data-testid={`w57-final-${i}`}>
                <div className={`inline-flex w-14 h-14 rounded-full items-center justify-center font-mono font-black text-xl text-white ${t.bg} ${t.ring} shadow-lg`}>
                  {n.number}
                </div>
                <div className="text-[9px] font-bold text-amber-300 mt-1.5">{t.label}</div>
                {/* PRIMARY % — pool match rate */}
                <div className="text-[11px] font-mono font-black text-emerald-300 mt-0.5">{n.pool_match_rate_pct}%</div>
                {/* Digit group with its projected chance % */}
                <div className="text-[8px] text-slate-300 font-mono mt-0.5">
                  d{n.digit_group}{n.in_top3_dg ? '✓' : ''} {driverDigit ? `· ${driverDigit.chance_pct}%` : ''}
                </div>
                {n.reappearance_freq > 0 && (
                  <div className="text-[8px] text-violet-300 font-bold mt-0.5">★{n.reappearance_freq}× date</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 text-[10px] text-slate-400 italic leading-relaxed">
          <span className="text-emerald-300 font-bold">Pool-match %</span> = empirical historical hit rate of that pool × digit alignment (Same-Date+top-3 = 60.7%, Fresh+top-3 = 31.1%, Both = 28.2%, Recent-5 = 6.2%).{' '}
          <span className="text-amber-300 font-bold">Projected digit %</span> = chance of that digit group hitting next draw (gap-driven, headline metric).
        </div>
      </div>
    </section>
  );
}
